import React, { useState, useEffect, useRef } from 'react';
import { getAQIColor, getAQILabel } from './utils/aqiUtils';
import { useTheme } from './context/ThemeContext';
import airQualityIcon from './assets/air-quality-icon.svg';
import arrowUpIcon from './assets/arrow-up.svg';

/**
 * Fixed detail card - EXACT match to reference image
 */
const DetailCard = ({ station, onClose, refreshTrigger }) => {
    const { isDark } = useTheme();
    const [currentStation, setCurrentStation] = useState(station);
    const lastPoleNumberRef = useRef(null);
    const [loading, setLoading] = useState(false);

    // Fetch individual pole data immediately and poll every 5 minutes while card is open
    useEffect(() => {
        if (!station?.poleNumber) return;

        const updateData = async (showLoadingState = false) => {
            if (showLoadingState) {
                setLoading(true);
            }
            try {
                const token = localStorage.getItem('accessToken');
                const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
                const response = await fetch(`${baseUrl}/polewise-current-air-quality/?pole_id=${station.poleNumber}&_t=${Date.now()}`, {
                    headers: {
                        'Authorization': token ? `Bearer ${token}` : '',
                        'Content-Type': 'application/json'
                    }
                });
                const json = await response.json();
                console.log(`fetchIndividualPoleData response for ${station.poleNumber}:`, json);

                if ((json.success || json.status) && json.data) {
                    const item = json.data;
                    const data = {
                        uid: item.pole_id,
                        poleNumber: item.pole_id,
                        name: item.location_name,
                        dailyVolumeCubicFeet: item.total_air_cleaned_cuft_last_24h ?? item.total_air_cleaned_so_far_today ?? item.dailyVolumeCubicFeet ?? 0,
                        // Extract actual API timestamp without falling back to local time/date
                        bucketStartUtc: item.DateStamp || item.timestamp || item.time || item.last_updated || item.lastUpdated || item.Date || item.date || item.created_at || null,
                        aqiBefore: {
                            'PM10': item.pm10_inlet ?? 0,
                            'PM2.5': item.pm2_5_inlet ?? 0,
                            'AQI': item.pm10_inlet ?? 0
                        },
                        aqiAfter: {
                            'PM10': item.pm10_outlet ?? 0,
                            'PM2.5': item.pm2_5_outlet ?? 0,
                            'AQI': item.pm10_outlet ?? 0
                        },
                        aqi: item.pm10_outlet ?? 0
                    };
                    setCurrentStation(data);
                }
            } catch (error) {
                console.error('Error fetching individual pole data in DetailCard:', error);
            }
            if (showLoadingState) {
                setLoading(false);
            }
        };

        const isNewPole = lastPoleNumberRef.current !== station.poleNumber;
        lastPoleNumberRef.current = station.poleNumber;

        if (isNewPole) {
            setCurrentStation(station);
        }
        updateData(isNewPole);
    }, [station?.poleNumber, refreshTrigger]);

    if (!currentStation) {
        return null;
    }

    const { name, location_name, poleNumber, aqiBefore, aqiAfter, dominentpol, time, bucketStartUtc, uid, dailyVolumeCubicFeet } = currentStation;
    // Representative AQI: use proper AQI if available, otherwise fallback
    const repAQI = aqiAfter?.AQI_24hrs ?? aqiAfter?.['PM2.5'] ?? aqiAfter?.['PM10'] ?? 0;
    const repColor = getAQIColor(repAQI);
    const repLabel = aqiAfter?.category ?? getAQILabel(repAQI);

    // Format time: "2026-02-04 10:00:00" → "10:00 AM - 04 Feb 2026" (in IST)
    const formatTime = (rawTime) => {
        if (!rawTime) return '';
        try {
            const date = new Date(rawTime);
            const formatter = new Intl.DateTimeFormat('en-IN', {
                timeZone: 'Asia/Kolkata',
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
            const parts = formatter.formatToParts(date);
            const getPart = (type) => parts.find(p => p.type === type)?.value;

            const month = getPart('month');
            const day = getPart('day');
            const year = getPart('year');
            const hour = getPart('hour');
            const minute = getPart('minute');
            const dayPeriod = getPart('dayPeriod')?.toUpperCase();

            // Handle invalid formatting parts gracefully
            if (!month || !day || !year || !hour || !minute) {
                return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
            }

            return `${hour}:${minute} ${dayPeriod} - ${day} ${month} ${year}`;
        } catch {
            return rawTime;
        }
    };

    return (
        <div className="detail-card-exact" style={{ padding: 20, minWidth: 330, maxWidth: 380, background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px #0001', fontFamily: 'inherit' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>Pole Number: {poleNumber}</span>
                <button className="close-btn-exact" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#222' }}>×</button>
            </div>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 16, color: '#0079bb' }}>
                Location: <span style={{ fontWeight: 400, fontSize: 12, color: '#3d3d3d' }}>
                    {loading ? (
                        <span className="detail-card-skeleton" style={{ width: 120, height: 14, display: 'inline-block', verticalAlign: 'middle', marginLeft: 4 }} />
                    ) : (
                        name
                    )}
                </span>
            </div>

            {/* AQI Before/After Table */}
            <div style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', fontWeight: 600, fontSize: 16, marginBottom: 4 }}>
                    <span style={{ flex: 2 }}>Pollutant</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>Before</span>
                    <span style={{ flex: 1, textAlign: 'center' }}>After</span>
                </div>
                {['PM10', 'PM2.5'].map((key) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', fontSize: 12, marginBottom: 2 }}>
                        <span style={{ flex: 2 }}>{key}</span>
                        <span style={{ flex: 1, textAlign: 'center' }}>
                            {loading ? (
                                <span className="detail-card-skeleton" style={{ width: 50, height: 14 }} />
                            ) : (
                                aqiBefore?.[key] !== undefined ? (typeof aqiBefore[key] === 'number' ? aqiBefore[key].toFixed(6) : Math.round(aqiBefore[key] * 100) / 100) : '—'
                            )}
                        </span>
                        <span style={{ flex: 1, textAlign: 'center' }}>
                            {loading ? (
                                <span className="detail-card-skeleton" style={{ width: 50, height: 14 }} />
                            ) : (
                                aqiAfter?.[key] !== undefined ? (typeof aqiAfter[key] === 'number' ? aqiAfter[key].toFixed(6) : Math.round(aqiAfter[key] * 100) / 100) : '—'
                            )}
                        </span>
                    </div>
                ))}
            </div>

            {/* Representative AQI summary */}
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <img src={airQualityIcon} alt="" style={{ width: 28, height: 28, marginRight: 8 }} />
                    <span style={{ fontSize: 12, color: '#888' }}>Total Air Cleaned (cu ft) <br />Last 24 Hours</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    {loading ? (
                        <span className="detail-card-skeleton" style={{ width: 80, height: 22, borderRadius: 4 }} />
                    ) : (
                        <span style={{ fontWeight: 700, fontSize: 22, color: repColor }}>
                            {dailyVolumeCubicFeet}
                        </span>
                    )}
                </div>
            </div>

            {/* Last Recorded */}
            <div style={{ background: '#f8f5ed', borderRadius: 10, padding: 6, fontSize: 11, color: '#a68c5c', fontWeight: 500, textAlign: 'center', border: '1.5px solid #eee' }}>
                Last Recorded: {loading ? (
                    <span className="detail-card-skeleton" style={{ width: 140, height: 11, verticalAlign: 'middle' }} />
                ) : (
                    formatTime(bucketStartUtc) || '—'
                )}
            </div>
        </div>
    );
};

export default DetailCard;
